import { Link } from 'react-router-dom'
import { ArrowUpRightIcon } from '@heroicons/react/24/outline'
import { twMerge } from 'tailwind-merge'
import { H2, H3, Paragraph, SmallParagraph } from '../../components/typography/Typography'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'
import stadium from '../../assets/industries/stadium.png'
import auditorium from '../../assets/industries/auditorium.png'
import school from '../../assets/industries/school.png'
import corporate from '../../assets/industries/corporate.png'
import government from '../../assets/industries/government.png'

const industries = [
    { href: '/industries/stadium', label: 'Stadium', description: 'Outdoor seating for cricket stadiums, football fields, and more', image: stadium },
    { href: '/industries/auditorium', label: 'Auditorium', description: 'Motorized systems for movie theatres and black box theatres', image: auditorium },
    { href: '/industries/school', label: 'School', description: 'Motorized and manual gymnasium bleachers, indoor and outdoor', image: school },
    { href: '/industries/corporate', label: 'Corporate', description: 'Multi-purpose convention centers and halls', image: corporate },
    { href: '/industries/government', label: 'Government', description: 'Quick installation and warehousing for events', image: government },
]

const trackClick = (label: string, href: string) =>
    track(ANALYTICS_EVENTS.HOME_INDUSTRY_CARD_CLICKED, { industry: label, destination: href })

export const IndustriesSection = () => {
    return (
        <>
            <IndustriesBento />
            <IndustriesGrid />
        </>
    )
}

// Mobile / tablet: a bento of image tiles — one hero, a tall/stacked pair, and a
// wide closer — laid out on a 2-column grid of 7rem rows. Auto-placement fills
// the holes, so the tile classes below are index-keyed and order-dependent.
const BENTO_TILES = [
    'col-span-2 row-span-2',
    'col-span-1 row-span-2',
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
    'col-span-2 row-span-1',
]

const IndustriesBento = () => {
    return (
        <section className='flex flex-col px-4 lg:hidden'>
            <H2>Where it goes</H2>
            <Paragraph className='mt-2 text-gray-600'>
                Five venue types, one system.
            </Paragraph>

            <div className='mt-6 grid auto-rows-[7rem] grid-cols-2 gap-3'>
                {industries.map(({ href, label, description, image }, index) => {
                    // Only the hero tile is tall enough to carry the description.
                    const isHero = index === 0

                    return (
                        <Link
                            key={href}
                            to={href}
                            onClick={() => trackClick(label, href)}
                            className={twMerge(
                                'group relative overflow-hidden rounded-xl',
                                BENTO_TILES[index] ?? 'col-span-1 row-span-1'
                            )}
                        >
                            <img
                                src={image}
                                alt={label}
                                loading='lazy'
                                decoding='async'
                                className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                            />
                            <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent' />
                            <div className='absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-4'>
                                <H3 className='text-lg font-medium text-white'>{label}</H3>
                                {isHero && <SmallParagraph className='text-gray-200'>{description}</SmallParagraph>}
                            </div>
                        </Link>
                    )
                })}
            </div>
        </section>
    )
}

// Desktop: the full card grid, all venues visible at once.
const IndustriesGrid = () => {
    return (
        <div className='hidden lg:flex flex-col items-start gap-8 px-24'>
            <div className='flex flex-col gap-4'>
                <H2>Industries We Serve</H2>
                <Paragraph className='text-center'>
                    From national stadiums to school gymnasiums, we design seating around how the venue is actually used.
                </Paragraph>
            </div>
            <div className='grid w-full grid-cols-3 gap-4'>
                {industries.map(({ href, label, description, image }, index) => (
                    <Link
                        key={href}
                        to={href}
                        onClick={() => trackClick(label, href)}
                        className={`group relative h-64 overflow-hidden rounded-xl ${index === 0 ? 'col-span-2' : ''}`}
                    >
                        <img
                            src={image}
                            alt={label}
                            loading='lazy'
                            decoding='async'
                            className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                        />
                        <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent' />
                        <div className='absolute right-4 top-4 flex size-9 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100'>
                            <ArrowUpRightIcon className='size-4' />
                        </div>
                        <div className='absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-5'>
                            <H3 className='text-white font-medium'>{label}</H3>
                            <SmallParagraph className='text-gray-200'>{description}</SmallParagraph>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    )
}
